import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { S, TONES, DARK } from '~/app/ui/tokens';

interface ClickPoint {
  x: number;
  y: number;
  count: number;
}

type DeviceFilter = 'all' | 'desktop' | 'mobile' | 'tablet';

const DEVICE_WIDTHS: Record<DeviceFilter, number> = {
  all: 0,
  desktop: 1200,
  tablet: 768,
  mobile: 390,
};

interface HeatmapProps {
  path: string;
  clicks: Array<{
    data: {
      x: number;
      y: number;
      pageX: number;
      pageY: number;
      element?: string;
      text?: string;
    };
    timestamp?: number;
    device?: string;
    viewport?: {
      width: number;
      height: number;
    } | null;
  }>;
  version?: string;
  deviceFilter?: DeviceFilter;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const heatColorRGB = (t: number): [number, number, number] => {
  const tt = clamp01(t);
  if (tt <= 0.5) {
    const k = tt / 0.5;
    return [Math.round(lerp(0, 255, k)), 255, 0];
  }
  const k = (tt - 0.5) / 0.5;
  return [255, Math.round(lerp(255, 0, k)), 0];
};

const Heatmap: React.FC<HeatmapProps> = ({ path, clicks, version, deviceFilter = 'desktop' }) => {
  const classes = useStyles();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // We use this ref to cache the scrolling element so we don't have to search the DOM every single frame
  const cachedScrollerRef = useRef<Element | null>(null);

  const [containerWidth, setContainerWidth] = useState(0);

  const PREVIEW_HEIGHT = 600;
  
  const targetDeviceWidth = DEVICE_WIDTHS[deviceFilter];
  const previewWidth = (deviceFilter === 'mobile' || deviceFilter === 'tablet')
    ? Math.min(targetDeviceWidth, containerWidth)
    : containerWidth;
  
  const isMobileOrTablet = deviceFilter === 'mobile' || deviceFilter === 'tablet';

  const CELL_SIZE = 26;
  const BASE_RADIUS = 55;
  const MAX_RADIUS = 135;
  const EDGE_SOFTEN_BLUR = 10;
  const INTENSITY_GAMMA = 0.55;
  const MAX_HEATMAP_BLOBS = 350;

  const aggregateClicks = useCallback(
    (raw: HeatmapProps['clicks'], targetWidth: number): ClickPoint[] => {
      const grid: Record<string, ClickPoint> = {};

      for (const c of raw) {
        const { pageX, pageY } = c.data || {};
        if (typeof pageX !== 'number' || typeof pageY !== 'number') continue;
        // Skip invalid coordinates (NaN, Infinity)
        if (!isFinite(pageX) || !isFinite(pageY)) continue;

        // Calculate percentage-based X position if viewport data is available
        // This allows proper scaling when viewing heatmap at different widths
        const viewportWidth = c.viewport?.width;
        const xPercent = viewportWidth && viewportWidth > 0 
          ? pageX / viewportWidth 
          : undefined;

        // For aggregation, scale to target width to cluster similar clicks
        // If no viewport data, fall back to absolute pageX
        const scaledX = xPercent !== undefined && targetWidth > 0
          ? xPercent * targetWidth
          : pageX;

        const cellX = Math.floor(scaledX / CELL_SIZE) * CELL_SIZE;
        const cellY = Math.floor(pageY / CELL_SIZE) * CELL_SIZE;
        const key = `${cellX},${cellY}`;

        if (grid[key]) {
          grid[key].count += 1;
        } else {
          grid[key] = {
            x: cellX + CELL_SIZE / 2,
            y: cellY + CELL_SIZE / 2,
            count: 1,
          };
        }
      }

      return Object.values(grid);
    },
    [CELL_SIZE]
  );

  const getTrueScrollTop = (win: Window): number => {
    // IMPORTANT: Check for scroll containers FIRST before falling back to window scroll
    // Pages with scroll-snap often have div#content scrolling while window.scrollY stays near 0
    
    // 1. Check Cached Scroller (Optimization - check this FIRST)
    // BUT: Verify it's still part of the current window's document (not stale after resize/reload)
    if (cachedScrollerRef.current && 
        cachedScrollerRef.current.scrollTop > 0 &&
        cachedScrollerRef.current.ownerDocument === win.document) {
      return cachedScrollerRef.current.scrollTop;
    }
    
    // Cache is stale, clear it
    cachedScrollerRef.current = null;

    // 2. Hard Scan: Look for the "Scroll Snap" container or main scrolling element
    // Check for common IDs/patterns: #content, #app, body's direct children
    const doc = win.document;
    
    // Try common scroll container IDs first
    const contentDiv = doc.getElementById('content');
    if (contentDiv && contentDiv.scrollTop > 0) {
      cachedScrollerRef.current = contentDiv;
      return contentDiv.scrollTop;
    }
    
    const appDiv = doc.getElementById('app');
    if (appDiv && appDiv.scrollTop > 0) {
      cachedScrollerRef.current = appDiv;
      return appDiv.scrollTop;
    }
    
    // Scan body's children for scrolling containers
    const candidates = Array.from(doc.body.children);
    for (const child of candidates) {
      // It must be scrolled, and it must be tall enough to be the main page (ignore small divs)
      if (child.scrollTop > 0 && child.scrollHeight > win.innerHeight) {
        cachedScrollerRef.current = child; // Cache it for next time
        return child.scrollTop;
      }
      
      // Optional: Check one level deeper (common in some frameworks)
      if (child.children.length > 0) {
        for (const grandChild of Array.from(child.children)) {
           if (grandChild.scrollTop > 0 && grandChild.scrollHeight > win.innerHeight) {
              cachedScrollerRef.current = grandChild;
              return grandChild.scrollTop;
           }
        }
      }
    }

    // 3. Check Standard Document/Body scroll
    const docScroll = doc.documentElement?.scrollTop || doc.body?.scrollTop || 0;
    if (docScroll > 0) return docScroll;

    // 4. Finally, check Window scroll (least reliable for scroll-snap pages)
    return win.scrollY || 0;
  };

  const aggregatedPoints = useMemo(() => aggregateClicks(clicks, previewWidth), [aggregateClicks, clicks, previewWidth]);

  const displayedPoints = useMemo(() => {
    const allPoints = aggregatedPoints;
    if (allPoints.length <= MAX_HEATMAP_BLOBS) {
      return allPoints;
    }

    return allPoints
      .slice()
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_HEATMAP_BLOBS)
      .sort((a, b) => a.count - b.count);
  }, [aggregatedPoints]);

  const drawHeatmap = useCallback(() => {
    if (!canvasRef.current || !iframeRef.current || !previewWidth) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const win = iframeRef.current.contentWindow;
    if (!win) return;

    // USE THE ROBUST HELPER
    const scrollTop = getTrueScrollTop(win);
    const scrollLeft = win.scrollX || 0;

    const dpr = window.devicePixelRatio || 1;
    const pixelW = Math.max(1, Math.floor(previewWidth * dpr));
    const pixelH = Math.max(1, Math.floor(PREVIEW_HEIGHT * dpr));

    if (canvas.width !== pixelW) canvas.width = pixelW;
    if (canvas.height !== pixelH) canvas.height = pixelH;
    canvas.style.width = `${previewWidth}px`;
    canvas.style.height = `${PREVIEW_HEIGHT}px`;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, previewWidth, PREVIEW_HEIGHT);

    const points = displayedPoints;
    if (points.length === 0) return;

    const maxCount = Math.max(...points.map(p => p.count), 1);
    points.sort((a, b) => a.count - b.count);

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    (ctx as any).filter = EDGE_SOFTEN_BLUR > 0 ? `blur(${EDGE_SOFTEN_BLUR}px)` : 'none';

    for (const p of points) {
      // p.x is already scaled to previewWidth during aggregation
      // No need to rescale here - just apply scroll offset
      const x = p.x - scrollLeft;
      const y = p.y - scrollTop; 

      const raw = Math.min(1, p.count / maxCount);
      const intensity = Math.pow(raw, INTENSITY_GAMMA);

      const radius = BASE_RADIUS + (MAX_RADIUS - BASE_RADIUS) * intensity;
      const pad = radius + EDGE_SOFTEN_BLUR * 3;

      if (x < -pad || x > previewWidth + pad || y < -pad || y > PREVIEW_HEIGHT + pad) continue;

      const [cr, cg, cb] = heatColorRGB(intensity);
      const a = 0.22 + 0.78 * intensity;

      const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
      const warmA = a * (0.70 + 0.30 * intensity);
      
      g.addColorStop(0.00, `rgba(${cr},${cg},${cb},${a * 1.0})`);
      g.addColorStop(0.16, `rgba(255,190,0,${warmA})`);
      g.addColorStop(0.34, `rgba(255,255,0,${a * 0.70})`);
      g.addColorStop(0.62, `rgba(0,255,0,${a * 0.55})`);
      g.addColorStop(1.00, 'rgba(0,255,0,0)');

      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
    (ctx as any).filter = 'none';
    ctx.globalCompositeOperation = 'source-over';
  }, [displayedPoints, previewWidth]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let boundWin: Window | null = null;
    let rafId: number | null = null;

    const onScroll = () => {
      // Just trigger the redraw. The draw function will hunt for the scroll value.
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        drawHeatmap();
      });
    };

    const attachScrollListener = (win: Window) => {
      // Clear cached scroller on new document
      cachedScrollerRef.current = null;
      // Capture is ESSENTIAL to detect scroll on inner divs
      win.addEventListener('scroll', onScroll, { passive: true, capture: true });
      drawHeatmap();
    };

    const onLoad = () => {
      try {
        boundWin = iframe.contentWindow;
        if (!boundWin) return;
        attachScrollListener(boundWin);
      } catch {
        // cross-origin
      }
    };

    // Attach listener immediately if iframe is already loaded
    try {
      if (iframe.contentWindow && iframe.contentWindow.document.readyState === 'complete') {
        boundWin = iframe.contentWindow;
        attachScrollListener(boundWin);
      }
    } catch {
      // cross-origin or not ready
    }

    iframe.addEventListener('load', onLoad);

    return () => {
      iframe.removeEventListener('load', onLoad);
      if (boundWin) {
        boundWin.removeEventListener('scroll', onScroll as any, { capture: true });
      }
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [drawHeatmap]);

  useEffect(() => {
    drawHeatmap();
  }, [drawHeatmap]);

  useEffect(() => {
    if (!containerRef.current) return;
    const updateWidth = () => setContainerWidth(containerRef.current?.getBoundingClientRect().width || 0);
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const iframeSrc = version ? `${path}?preview=1&v=${version}` : `${path}?preview=1`;

  const deviceLabel = deviceFilter === 'all' ? 'All Devices' 
    : deviceFilter === 'desktop' ? 'Desktop (1200px)'
    : deviceFilter === 'tablet' ? 'Tablet (768px)'
    : 'Mobile (390px)';

  return (
    <div className={classes.container}>
      <div className={classes.legend}>
        <span className={classes.legendLabel}>Click Density:</span>
        <span className={classes.legendLow}>Low</span>
        <div className={classes.legendGradient} />
        <span className={classes.legendHigh}>High</span>
        <span className={classes.deviceIndicator}>
          <i className="material-icons">
            {deviceFilter === 'mobile' ? 'smartphone' : deviceFilter === 'tablet' ? 'tablet' : 'computer'}
          </i>
          {deviceLabel}
        </span>
      </div>

      <div ref={containerRef} className={classes.previewContainer}>
        <div 
          className={`${classes.deviceFrame} ${isMobileOrTablet ? classes.deviceFrameActive : ''}`}
          style={{
            width: previewWidth > 0 ? previewWidth : '100%',
          }}
        >
          {isMobileOrTablet && (
            <div className={classes.deviceFrameHeader}>
              <div className={classes.deviceNotch} />
            </div>
          )}
          <div className={classes.iframeWrapper}>
            <iframe 
              ref={iframeRef} 
              src={iframeSrc} 
              className={classes.iframe} 
              title="Page Preview"
              style={{ width: previewWidth > 0 ? previewWidth : '100%' }}
            />
            <canvas 
              ref={canvasRef} 
              className={classes.canvas}
              style={{ 
                width: previewWidth > 0 ? previewWidth : '100%',
              }}
            />
          </div>
        </div>
      </div>

      <div className={classes.stats}>
        <span>Total Clicks: {clicks.length}</span>
        <span>Hot Zones: {aggregatedPoints.length}</span>
      </div>
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 15px',
    backgroundColor: 'hsl(0, 0%, 95%)',
    borderRadius: '6px',
    flexWrap: 'wrap',
    [DARK]: { backgroundColor: S.chromeHover },
  },
  legendLabel: {
    fontSize: '13px',
    color: 'hsl(0, 0%, 50%)',
    [DARK]: { color: S.muted },
  },
  legendGradient: {
    flex: '0 0 auto',
    height: '12px',
    borderRadius: '6px',
    background:
      'linear-gradient(to right, rgba(0,255,0,0.85), rgba(255,255,0,0.95), rgba(255,0,0,1))',
    width: '180px',
  },
  legendLow: {
    fontSize: '12px',
    color: 'hsl(0, 0%, 50%)',
    [DARK]: { color: S.muted },
  },
  legendHigh: {
    fontSize: '12px',
    color: 'hsl(0, 0%, 50%)',
    [DARK]: { color: S.muted },
  },
  deviceIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto',
    padding: '4px 10px',
    backgroundColor: 'hsl(200, 73%, 95%)',
    borderRadius: '4px',
    fontSize: '12px',
    color: 'hsl(200, 73%, 33%)',
    fontWeight: '500',
    '& .material-icons': {
      fontSize: '16px',
    },
    [DARK]: { backgroundColor: TONES.blue.bg, color: S.blue },
  },
  previewContainer: {
    position: 'relative',
    height: '600px',
    borderRadius: '8px',
    border: '1px solid hsl(0, 0%, 88%)',
    backgroundColor: 'hsl(0, 0%, 92%)',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    [DARK]: { borderColor: S.line, backgroundColor: S.wall },
  },
  deviceFrame: {
    position: 'relative',
    height: '100%',
    backgroundColor: '#fff',
    transition: 'all 0.3s ease',
  },
  deviceFrameActive: {
    borderLeft: '2px solid hsl(0, 0%, 75%)',
    borderRight: '2px solid hsl(0, 0%, 75%)',
    boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
    [DARK]: { borderLeftColor: S.fieldLine, borderRightColor: S.fieldLine },
  },
  deviceFrameHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '20px',
    backgroundColor: 'hsl(0, 0%, 20%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  deviceNotch: {
    width: '60px',
    height: '6px',
    backgroundColor: 'hsl(0, 0%, 10%)',
    borderRadius: '3px',
  },
  iframeWrapper: {
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
  },
  iframe: {
    height: '100%',
    border: 'none',
    display: 'block',
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    pointerEvents: 'none',
    mixBlendMode: 'multiply',
    opacity: 1,
  },
  stats: {
    display: 'flex',
    gap: '20px',
    fontSize: '13px',
    color: 'hsl(0, 0%, 50%)',
    [DARK]: { color: S.muted },
  },
});

export default Heatmap;
